import { Flex, TextArea, Toast } from '@byecode/ui'
import { DevicePreviewEnum } from '@lighthouse/core'
import {
    DEFAULT_LANG,
    LanguageProvider,
    miniProgramScreenshotsSizeLimit,
    miniProgramVideoSizeLimit,
    WithoutPopperFileUploader
} from '@lighthouse/shared'
import * as React from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import styled, { css } from 'styled-components'

import type { WxMiniProgramsAuditInfo } from '@/services/types'
import { uploadManagerInAppParams, uploadMiniProgramResourceParams } from '@/utils/auth'

const SCxTitle = styled.div`
    color: var(--color-black);
    font-size: 24px;
    line-height: 32px;
    font-weight: 500;
    margin-bottom: 8px;
`

const SCxDescribe = styled.div`
    color: var(--color-gray-400);
    font-size: var(--font-size-normal);
    line-height: 22px;
`

const SCxContent = styled.div`
    width: 100%;
    padding: 24px 32px;
    display: flex;
    flex-direction: column;
    align-items: stretch;
`

const SCxSubTitle = styled.div<{ required?: boolean }>`
    font-weight: 600;
    line-height: 22px;
    font-size: var(--font-size-normal);
    ${({ required }) =>
        required &&
        css`
            &::after {
                content: '*';
                color: var(--color-red-500);
                margin-left: 4px;
            }
        `}
`
const SCxBox = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 8px 0;
    margin-top: 16px;
`

const SCxCounter = styled.div`
    position: absolute;
    right: 8px;
    bottom: 12px;
    color: var(--color-gray-400);
    line-height: 22px;
    font-size: var(---font-size-normal);
`
interface MiniProgramsInfoProps {}

const MAX_LENGTH = 200

const MiniProgramsInfo: React.FunctionComponent<MiniProgramsInfoProps> = props => {
    const { control } = useFormContext<WxMiniProgramsAuditInfo>()

    return (
        <LanguageProvider value={{ language: DEFAULT_LANG, languageList: [] }}>
            <SCxContent>
                <SCxTitle>第二步：小程序信息</SCxTitle>
                <SCxDescribe>提交审核前，请您需完善并确认下列信息，确保审核通过率（审核时间约1-7天）</SCxDescribe>
                {/* <SCxBox>
                    <SCxSubTitle required>版本号</SCxSubTitle>
                    <Input size="lg" {...register('version', { required: true })} />
                </SCxBox> */}
                <SCxBox>
                    <Flex gap={4} direction="column">
                        <SCxSubTitle required>版本描述</SCxSubTitle>
                        <SCxDescribe>请简要描述本次小程序的功能点。</SCxDescribe>
                    </Flex>
                    <div style={{ position: 'relative', width: '100%' }}>
                        <Controller
                            name="description"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <TextArea
                                        value={field.value}
                                        style={{ width: '100%', height: 124 }}
                                        placeholder="请输入"
                                        onChange={e => {
                                            const newValue = e.target.value
                                            if (newValue.length <= MAX_LENGTH) {
                                                field.onChange(newValue)
                                            }
                                        }}
                                    />
                                    <SCxCounter>
                                        {field.value?.length ?? 0}/{MAX_LENGTH}
                                    </SCxCounter>
                                </>
                            )}
                        />
                    </div>
                </SCxBox>
                <SCxBox>
                    <Flex gap={4} direction="column">
                        <SCxSubTitle>小程序页面截图</SCxSubTitle>
                        <SCxDescribe>
                            选填，可上传小程序截图，最多上传10张图片。图片支持jpg、jpeg、bmp、gif或png格式，图片大小不超过2M，建议可以提现出本次更新的主要内容。
                        </SCxDescribe>
                    </Flex>
                    <Controller
                        name="screenshots"
                        control={control}
                        render={({ field }) => (
                            <WithoutPopperFileUploader
                                uploadyOptions={{
                                    info: { id: '', label: '', groupId: '' },
                                    options: {
                                        ...uploadMiniProgramResourceParams(),
                                        // 最多上传10张图片。图片支持jpg、jpeg、bmp、gif或png格式，图片大小不超过5M
                                        fileFilter: (file: File | string, index: number) => {
                                            if (file instanceof File) {
                                                const imageFileRegex = /\.(jpg|jpeg|bmp|gif|png)$/i
                                                if (!imageFileRegex.test(file.name.toLocaleLowerCase())) {
                                                    Toast.error('只能上传jpg、jpeg、bmp、gif或png格式的图片')
                                                    return false
                                                }
                                                if (file.size > miniProgramScreenshotsSizeLimit) {
                                                    Toast.error('图片大小不能超过2MB')
                                                    return false
                                                }
                                            }
                                            return true
                                        }
                                    }
                                }}
                                accept="image/jpeg,image/jpg,image/png,image/gif,image/bmp"
                                enableItemActions={{
                                    canDelete: true,
                                    canDownload: true,
                                    canPreview: true
                                }}
                                previewType={DevicePreviewEnum.DESKTOP}
                                limit={10}
                                onChange={v => {
                                    field.onChange(v)
                                }}
                                uploadFileType="file"
                                files={field.value || []}
                            />
                        )}
                    />
                </SCxBox>
                <SCxBox>
                    <Flex gap={4} direction="column">
                        <SCxSubTitle>小程序录屏</SCxSubTitle>
                        <SCxDescribe>选填，可上传小程序使用录屏，最多上传1个视频。视频支持mp4格式，视频大小不超过10M。</SCxDescribe>
                    </Flex>
                    <Controller
                        name="screenRecords"
                        control={control}
                        render={({ field }) => (
                            <WithoutPopperFileUploader
                                previewType={DevicePreviewEnum.DESKTOP}
                                uploadyOptions={{
                                    info: { id: '', label: '', groupId: '' },
                                    options: {
                                        ...uploadManagerInAppParams(),
                                        // 视频大小不超过20M
                                        fileFilter: (file: File | string, index: number) => {
                                            if (file instanceof File) {
                                                if (!file.name.toLocaleLowerCase().includes('mp4')) {
                                                    Toast.error('只能上传mp4格式的视频')
                                                    return false
                                                }
                                                if (file.size > miniProgramVideoSizeLimit) {
                                                    Toast.error('视频大小不能超过10MB')
                                                    return false
                                                }
                                            }
                                            return true
                                        }
                                    }
                                }}
                                accept="video/mp4"
                                enableItemActions={{
                                    canDelete: true,
                                    canDownload: true,
                                    canPreview: true
                                }}
                                limit={1}
                                onChange={field.onChange}
                                uploadFileType="video"
                                files={field.value || []}
                            />
                        )}
                    />
                </SCxBox>
                <SCxBox>
                    <Flex gap={4} direction="column">
                        <SCxSubTitle>测试备注</SCxSubTitle>
                        <SCxDescribe>选填，若测试流程特殊，请描述测试流程以便微信审核人员进行审核。</SCxDescribe>
                    </Flex>
                    <div style={{ position: 'relative', width: '100%' }}>
                        <Controller
                            name="testNotes"
                            control={control}
                            render={({ field }) => (
                                <>
                                    <TextArea
                                        value={field.value}
                                        style={{ width: '100%', height: 124 }}
                                        placeholder="请输入"
                                        onChange={e => {
                                            const newValue = e.target.value
                                            if (newValue?.length <= MAX_LENGTH) {
                                                field.onChange(newValue)
                                            }
                                        }}
                                    />
                                    <SCxCounter>
                                        {field.value?.length ?? 0}/{MAX_LENGTH}
                                    </SCxCounter>
                                </>
                            )}
                        />
                    </div>
                </SCxBox>
            </SCxContent>
        </LanguageProvider>
    )
}

export default MiniProgramsInfo
